import { postJson } from "@lib/api";
import { onReady, getById } from "@lib/dom";
import { convertUtcToLocal } from "@lib/datetime";
import type { PurchasePageData } from "../types";
import type { PaymentCalculationRequest } from "../types/generated/PaymentCalculationRequest";
import type { PaymentCalculationResponse } from "../types/generated/PaymentCalculationResponse";

interface PriceUpdateElements {
  ticketCountInput: HTMLInputElement;
  basePriceElement: HTMLElement;
  feesPriceElement: HTMLElement;
  totalPriceElement: HTMLElement;
  feesLineElement: HTMLElement;
  errorMessage: HTMLElement;
}

async function updatePricing(
  eventId: string,
  discountToken: string | undefined,
  elements: PriceUpdateElements,
): Promise<void> {
  const ticketCount = parseInt(elements.ticketCountInput.value) || 1;

  try {
    const request: PaymentCalculationRequest = {
      event_id: eventId,
      ticket_count: ticketCount,
      door_sale: false,
      discount_token: discountToken ?? null,
    };

    const data = await postJson<PaymentCalculationResponse>(
      "/payment/calculate-price",
      request,
    );

    const baseAmount = data.amounts.base_amount / 100;
    const fees = data.amounts.fees / 100;
    const total = data.amounts.customer_pays / 100;

    elements.basePriceElement.textContent = `$${baseAmount.toFixed(2)}`;
    elements.totalPriceElement.textContent = `$${total.toFixed(2)}`;

    if (data.fees_passed_to_customer && fees > 0) {
      elements.feesPriceElement.textContent = `$${fees.toFixed(2)}`;
      elements.feesLineElement.style.display = "flex";
    } else {
      elements.feesLineElement.style.display = "none";
    }

    elements.errorMessage.classList.add("hidden");
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Error calculating price";
    showError(elements.errorMessage, message);
  }
}

function showError(errorElement: HTMLElement, messageText: string): void {
  errorElement.textContent = messageText;
  errorElement.classList.remove("hidden");
  setTimeout(() => {
    errorElement.classList.add("hidden");
  }, 5000);
}

function initPurchasePage(data: PurchasePageData): void {
  const elements: PriceUpdateElements = {
    ticketCountInput: getById<HTMLInputElement>("ticket-count"),
    basePriceElement: getById("base-price"),
    feesPriceElement: getById("fees-price"),
    totalPriceElement: getById("total-price"),
    feesLineElement: getById("fees-line"),
    errorMessage: getById("error-message"),
  };

  elements.ticketCountInput.addEventListener("input", () =>
    updatePricing(data.eventId, data.discountToken, elements),
  );
  elements.ticketCountInput.addEventListener("blur", () =>
    updatePricing(data.eventId, data.discountToken, elements),
  );

  updatePricing(data.eventId, data.discountToken, elements);
  convertUtcToLocal();
}

window.initPurchase = (data: PurchasePageData) => {
  onReady(() => initPurchasePage(data));
};
